import { useState, useEffect, useCallback } from 'react';
import * as notificationApi from '../services/notificationApi';
import { Notification } from '../services/notificationApi';
import apiService from '../services/api';

export const useNotificationList = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch notifications
   */
  const fetchNotifications = useCallback(async (params?: { isRead?: boolean; type?: string }) => {
    setLoading(true);
    setError(null);
    try {
      const response = await notificationApi.getNotifications(params);
      setNotifications(response.notifications);
    } catch (err: any) {
      setError(err.message || 'Failed to fetch notifications');
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch unread count
   */
  const fetchUnreadCount = useCallback(async () => {
    try {
      const count = await notificationApi.getUnreadCount();
      setUnreadCount(count);
    } catch (err: any) {
      // Failed to fetch unread count
    }
  }, []);

  /**
   * Mark notification as read
   */
  const markAsRead = useCallback(async (notificationId: string) => {
    try {
      await notificationApi.markAsRead(notificationId);

      // Update local state
      setNotifications(prev =>
        prev.map(n =>
          n._id === notificationId
            ? { ...n, isRead: true, readAt: new Date().toISOString() }
            : n
        )
      );

      // Decrease unread count
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err: any) {
      throw err;
    }
  }, []);

  /**
   * Mark all notifications as read
   */
  const markAllAsRead = useCallback(async () => {
    try {
      await notificationApi.markAllAsRead();

      // Update local state
      setNotifications(prev =>
        prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() }))
      );
      setUnreadCount(0);
    } catch (err: any) {
      throw err;
    }
  }, []);

  /**
   * Delete notification
   */
  const deleteNotification = useCallback(async (notificationId: string) => {
    try {
      // Check if notification was unread
      const notification = notifications.find(n => n._id === notificationId);
      const wasUnread = notification && !notification.isRead;

      await notificationApi.deleteNotification(notificationId);

      // Remove from local state
      setNotifications(prev => prev.filter(n => n._id !== notificationId));

      // Decrease unread count if it was unread
      if (wasUnread) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err: any) {
      throw err;
    }
  }, [notifications]);

  /**
   * Delete all read notifications
   */
  const deleteAllRead = useCallback(async () => {
    try {
      await notificationApi.deleteAllRead();

      // Remove read notifications from local state
      setNotifications(prev => prev.filter(n => !n.isRead));
    } catch (err: any) {
      throw err;
    }
  }, []);

  /**
   * Refresh notifications and unread count
   */
  const refresh = useCallback(async () => {
    await Promise.all([
      fetchNotifications(),
      fetchUnreadCount(),
    ]);
  }, [fetchNotifications, fetchUnreadCount]);

  // Auto-fetch unread count on mount (only if authenticated)
  useEffect(() => {
    const isAuthenticated = apiService.isAuthenticated();

    if (!isAuthenticated) {
      return; // Don't fetch if user is not logged in
    }

    fetchUnreadCount();

    // Poll for unread count every 30 seconds
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    loading,
    error,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    deleteAllRead,
    refresh,
  };
};
